import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Booking, Invitee } from "@/types/booking";
import { DialogTitle } from "@radix-ui/react-dialog";
import { RadioGroup } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const AddToCalendarButton = ({ booking }: { booking: Booking }) => {
  const { start_at, end_at, subject, description } = booking;
  if (!start_at && !end_at) return;

  const invitee = booking.invitees ? booking.invitees[0] : ({} as Invitee);
  const location = booking.location
    ? booking.location.join_url || booking.location.name
    : "dayschedule.com";
  const start = format(parseISO(start_at), "yyyyMMdd'T'HHmmss");
  const end = format(parseISO(end_at), "yyyyMMdd'T'HHmmss");
  const title = encodeURIComponent(subject!);
  const encodedTimeZone = encodeURIComponent(
    invitee ? invitee.time_zone : booking.time_zone
  );
  const encodedLocation = location ? encodeURIComponent(location) : "";
  const encodedDescription = description ? encodeURIComponent(description) : "";

  const calendarOptions = [
    {
      name: "Google",
      url: `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${encodedDescription}&ctz=${encodedTimeZone}&location=${encodedLocation}`,
      icon: "https://cdn.dayschedule.com/icon/google.png",
    },
    {
      name: "Outlook.com",
      url: `https://outlook.live.com/calendar/0/action/compose?rru=addevent&subject=${title}&body=${encodedDescription}&startdt=${start_at}&enddt=${end_at}&location=${encodedLocation}`,
      icon: "https://cdn.dayschedule.com/icon/microsoft-outlook.png",
    },
    {
      name: "Yahoo",
      url: `https://calendar.yahoo.com/?v=60&title=${title}&st=${start}&et=${end}&in_loc=${encodedLocation}&desc=${encodedDescription}`,
      icon: "https://cdn.dayschedule.com/icon/yahoo-icon.png",
    },
    {
      name: "Office 365",
      url: `https://outlook.office.com/calendar/0/action/compose?rru=addevent&startdt=${start_at}&enddt=${end_at}&subject=${title}&location=${encodedLocation}&body=${encodedDescription}`,
      icon: "https://cdn.dayschedule.com/icon/office-365.png",
    },
  ];

  const downloadICS = () => {
    const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:${subject}\nDTSTART:${format(
      new Date(start_at),
      "yyyyMMdd'T'HHmmssX"
    )}\nDTEND:${format(
      new Date(end_at),
      "yyyyMMdd'T'HHmmssX"
    )}\nLOCATION:${location}\nDESCRIPTION:${description}\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([icsContent], { type: "text/calendar" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "event.ics";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="mb-4 w-full">
          <Calendar className="h-4 w-4 mr-2" />
          Add to Calendar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogTitle>Add to Calendar</DialogTitle>
        <div className="pt-0 space-y-4">
          <RadioGroup className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {calendarOptions.map(({ name, url, icon }) => (
              <Label
                key={name}
                className="flex h-full flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer text-center"
              >
                <a
                  href={url}
                  target="_blank"
                  className="flex flex-col justify-center items-center gap-2"
                >
                  <img src={icon} alt={name} width={32} height={32} />
                  <span className="text-center text-sm truncate">{name}</span>
                </a>
              </Label>
            ))}
            <Label className="flex h-full flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer text-center">
              <button
                onClick={downloadICS}
                className="flex flex-col justify-center items-center gap-2"
              >
                <Calendar className="h-8 w-30" />
                <span className="text-center text-sm">iCal file</span>
              </button>
            </Label>
          </RadioGroup>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddToCalendarButton;
